import React, { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import { useRouter } from 'next/router';
import {
  useMutation,
  OperationVariables,
  ApolloQueryResult,
} from '@apollo/client';

import Icon from 'koba/components/Icon';

import {
  BREAKPOINT_MD,
  POSTS_PER_PAGE,
  NUMBER_OF_REACTIONS,
} from 'utils/constants';
import TrackingEvent from 'constants/trackingEvents';

import { POST_LIST_QUERY } from 'schema/Post/queries';
import { DANGER_TYPE } from 'koba/components/InlineActions/constants';
import { useCommunityContext } from 'components/CommunityContext';
import { canDeletePost, canEditPost, canPinPost } from 'utils/userHelpers';
import {
  FOLLOW_POST_MUTATION,
  UNFOLLOW_POST_MUTATION,
  PIN_POST_MUTATION,
  UNPIN_POST_MUTATION,
} from 'schema/Post/mutations';
import { useWindowSize } from 'hooks/useWindowSize';

import { TFunction } from 'next-i18next';
import {
  MentionedUsersFieldFragment,
  PostListQuery,
} from '__generated__/graphql/legacy/graphql';
import { PostListUser } from 'types/user';
import { Post as PostType, PostAction } from 'types/post';

import { eventNames } from 'constants/eventNames';
import { Attachment } from 'schema/Post/types';
import { copyToClipboard } from 'utils/textHelpers';
import PostHeader from './components/PostHeader';
import PostFooter from './components/PostFooter';
import ExpandableContent from './components/ExpandableContent';

import styles from './Post.module.scss';

const cx = classNames.bind(styles);
interface PostProps {
  content: string;
  mentionedUsers?: MentionedUsersFieldFragment | null;
  post: PostType;
  postReactionCount: number;
  refetchPostList?: (
    variables?: Partial<OperationVariables>
  ) => Promise<ApolloQueryResult<PostListQuery>>;
  showSpace?: boolean;
  t: TFunction;
  time: string;
  totalCommentCount: number;
  author?: PostListUser | null;
  onPostChange?(): void;
  handleDeletePost(event): void;
  handleEditPost(event): void;
  trackPostEvent(event): void;
  isReplyPanel?: boolean;
  showActions?: boolean;
}

const Post: React.FC<PostProps> = ({
  content,
  mentionedUsers,
  author,
  post,
  time,
  t,
  refetchPostList,
  totalCommentCount,
  postReactionCount,
  showSpace = false,
  onPostChange,
  handleDeletePost,
  handleEditPost,
  trackPostEvent,
  isReplyPanel = false,
  showActions = true,
}) => {
  const router = useRouter();
  const { community, currentUser } = useCommunityContext();
  const { width } = useWindowSize();
  const isMobile = width < BREAKPOINT_MD;
  const [shouldClampContent, setShouldClampContent] = useState<boolean>(
    (content?.length || 0) > 160
  );
  const commentId = router && router.query && (router.query.comment as string);

  useEffect(() => {
    if (commentId) {
      openReplyPanel();
    }
  }, [commentId]);

  const refetchQueries = [
    {
      query: POST_LIST_QUERY,
      variables: {
        communityId: community.id,
        numOfPosts: POSTS_PER_PAGE,
        filter: {
          following: true,
        },
        postOrderField: undefined,
        numberOfReactions: NUMBER_OF_REACTIONS,
      },
    },
  ];

  const [unfollowPost, { loading: unfollowLoading }] = useMutation(
    UNFOLLOW_POST_MUTATION,
    {
      refetchQueries,
    }
  );
  const [followPost, { loading: followLoading }] = useMutation(
    FOLLOW_POST_MUTATION,
    {
      refetchQueries,
    }
  );
  const [pinPost, { loading: pinLoading }] = useMutation(PIN_POST_MUTATION);
  const [unpinPost, { loading: unpinLoading }] = useMutation(
    UNPIN_POST_MUTATION
  );

  const openReplyPanel = () => {
    PubSub.publish(eventNames.OPEN_REPLY_PANEL, {
      lastElementRef: postRef,
      post,
      commentIdFromUrl: commentId,
    });
  };

  const copyPostUrl = () => {
    const currentUrl = window.location.origin;
    const postUrl = `${currentUrl}/communities/${community.id}/post/${post.id}`;
    copyToClipboard(
      postUrl,
      t('common-copyUrlSuccess', 'URL copied to clipboard'),
      t('common-copyUrlError', 'Failed to copy URL')
    );
  };

  const canUserEditPost = canEditPost(currentUser, author);
  const canUserDeletePost = canDeletePost(currentUser, author);
  const canUserPinPost = canPinPost(currentUser);

  const postRef = React.useRef<HTMLDivElement>(null);

  const postActionsList: PostAction[] = [
    {
      name: 'copyUrl',
      text: t('common-copyUrl', 'Copy URL'),
      onClick: copyPostUrl,
    },
  ];

  if (post.isFollowing && !unfollowLoading) {
    postActionsList.push({
      name: 'unfollow',
      text: `${t('Unfollow')}`,
      onClick: () => {
        unfollowPost({
          variables: { id: post.id, numberOfReactions: NUMBER_OF_REACTIONS },
        }).then(() => {
          onPostChange?.();
          trackPostEvent(TrackingEvent.UNFOLLOW);
        });
      },
    });
  } else if (!followLoading) {
    postActionsList.push({
      name: 'follow',
      text: t('Follow'),
      onClick: () => {
        followPost({
          variables: { id: post.id, numberOfReactions: NUMBER_OF_REACTIONS },
        }).then(() => {
          onPostChange?.();
          trackPostEvent(TrackingEvent.FOLLOW);
        });
      },
    });
  }

  if (canUserEditPost) {
    postActionsList.push({
      name: 'edit',
      text: t('common-edit', 'Edit'),
      onClick: handleEditPost,
    });
  }

  if (canUserPinPost) {
    if (post.pinnedAt && !unpinLoading) {
      postActionsList.push({
        name: 'unpin',
        text: t('Unpin'),
        onClick: () =>
          unpinPost({
            variables: { id: post.id, numberOfReactions: NUMBER_OF_REACTIONS },
          }).then(() => {
            onPostChange?.();
            trackPostEvent(TrackingEvent.UNPIN);
            refetchPostList?.({ shouldShowLoading: true });
          }),
      });
    } else if (!pinLoading) {
      postActionsList.push({
        name: 'pin',
        text: t('Pin'),
        onClick: () =>
          pinPost({
            variables: { id: post.id, numberOfReactions: NUMBER_OF_REACTIONS },
          }).then(() => {
            onPostChange?.();
            trackPostEvent(TrackingEvent.PIN);
            refetchPostList?.({ shouldShowLoading: true });
          }),
      });
    }
  }

  if (canUserDeletePost) {
    postActionsList.push({
      name: 'delete',
      text: (
        <div className={cx('post__detail-action')}>
          <Icon className={cx('post__detail-action__icon')} name="trash-can" />
          <span>{t('Delete post')}</span>
        </div>
      ),
      title: t('Delete post'),
      onClick: handleDeletePost,
      type: DANGER_TYPE,
    });
  }

  const hasAttachment = post.postAttachments && post.postAttachments[0];

  return (
    <div className={cx('post')}>
      <div
        aria-label={`${t('Read full post')}: ${post.title}`}
        className={cx('post__container', {
          'is-reply-panel': isReplyPanel,
        })}
        ref={postRef}
        role="button"
        tabIndex={0}
      >
        <div
          className={cx('post__detail', {
            'is-spaces-enabled': showSpace, // we should try and avoid coupling feature names to classes
          })}
          id={post.id}
          role="article"
        >
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
          <div
            className={cx('post__body', {
              'is-spaces-enabled': showSpace,
            })}
          >
            <PostHeader
              author={author}
              creationDateTime={time || ''}
              editedAt={post.editedAt}
              isFollowing={post.isFollowing}
              isMobile={isMobile}
              isReplyPanel={isReplyPanel}
              pinnedAt={post.pinnedAt}
              postActionsList={showActions ? postActionsList : []}
              showSpace={showSpace}
              spaceId={post.communitySpace?.id || ''}
              spaceName={post.communitySpace?.name || ''}
              title={post.title || ''}
              showFollowingTag
            />

            <ExpandableContent
              attachments={(post.postAttachments as Attachment[]) || []}
              content={content}
              hasAttachment={!!hasAttachment}
              isReplyPanel={isReplyPanel}
              mentionedUsers={mentionedUsers}
              setShouldClampContent={(shouldClamp) => {
                setShouldClampContent(shouldClamp);
              }}
              shouldClampContent={shouldClampContent}
              t={t}
              shouldScroll
            />
          </div>

          <PostFooter
            hasReacted={post.hasReacted}
            isReplyPanel={isReplyPanel}
            openReplyPanel={openReplyPanel}
            post={post}
            postReactionCount={postReactionCount}
            totalCommentCount={totalCommentCount}
          />
        </div>
      </div>
    </div>
  );
};

export default Post;
